/**
 * Phase 68 Plan 03 — PowerInstabilityBadge (Topbar D-OFF-03 channel 1).
 *
 * Mirrors StaleBadge.tsx (Phase 67-01 Wave 6) structurally: lucide icon
 * + count + colored pill + react-router Link to a filtered admin page.
 *
 * Behaviour contract (UI-SPEC §6.5):
 *   - Renders null when loading OR data missing OR count === 0
 *   - Renders Link with Zap icon + IT label (singular/plural) when count >= 1
 *   - On rising-edge (count went UP since last fetch), enables
 *     `data-pulse="true"` for 60s — visualises NEW flag transitions
 *     specifically (alert fatigue defense, UI-SPEC §11.4)
 *   - On initial mount with count > 0, NO pulse (suppresses navigation-
 *     to-fresh-page pulse)
 *   - Pulse class wrapped in Tailwind `motion-safe:` variant so users
 *     with `prefers-reduced-motion: reduce` get the static badge
 *     (WCAG 2.3.3, UI-SPEC §8.3)
 *
 * Colors come from the orange band tokens added in Task 1 (Wave 0):
 *   bg: var(--color-orange-bg)  text: var(--color-orange-strong)
 *
 * Tenant isolation handled upstream by `usePowerInstabilityCount`'s
 * `qk.powerInstabilityFlagged(circuitId)` key (Pitfall 8).
 */

import { useEffect, useRef, useState } from "react";
import { Zap } from "lucide-react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import { usePowerInstabilityCount } from "@/hooks/queries/usePowerInstabilityCount";

export default function PowerInstabilityBadge() {
  const { t } = useTranslation();
  const { data, isLoading } = usePowerInstabilityCount();
  // Track previous count; on rising-edge (NEW > PREV), pulse for 60s.
  // First-mount sentinel: previousCountRef.current === null until first
  // resolved data, so initial mount with count>0 sets the ref WITHOUT
  // pulsing.
  const previousCountRef = useRef<number | null>(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (isLoading || !data) return;
    const prev = previousCountRef.current;
    const next = data.count;
    if (prev === null) {
      // First resolved fetch — seed the ref WITHOUT pulsing, even if
      // count > 0 already (avoids alert-fatigue on every navigation).
      previousCountRef.current = next;
      return;
    }
    if (next > prev) {
      setPulse(true);
      const handle = window.setTimeout(() => setPulse(false), 60_000);
      previousCountRef.current = next;
      return () => window.clearTimeout(handle);
    }
    // Same or decreasing count — update the ref so the NEXT rising
    // edge fires correctly.
    previousCountRef.current = next;
  }, [data, isLoading]);

  if (isLoading || !data || data.count === 0) return null;

  const count = data.count;
  const label =
    count === 1
      ? t("admin.powerHealth.badge.labelSingular", { defaultValue: "1 instabile" })
      : t("admin.powerHealth.badge.labelPlural", {
          n: count,
          defaultValue: `${count} instabili`,
        });
  const aria = t("admin.powerHealth.badge.aria", {
    n: count,
    defaultValue: `Kiosk con instabilità di alimentazione: ${count}. Premi Invio per aprire Power Health.`,
  });
  const title = t("admin.powerHealth.badge.tooltip", {
    n: count,
    defaultValue: `${count} kiosk con instabilità di alimentazione (cluster acuto o severity 7g ≥ 10). Click per dettagli.`,
  });

  return (
    <Link
      to="/admin/power-health?filter=flagged"
      data-testid="power-instability-badge"
      data-pulse={pulse ? "true" : "false"}
      aria-label={aria}
      title={title}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium
                 bg-[color:var(--color-orange-bg)] text-[color:var(--color-orange-strong)]
                 hover:ring-1 hover:ring-[color:var(--color-orange-strong)]/30
                 transition-shadow data-[pulse=true]:motion-safe:animate-pulse"
    >
      <Zap className="w-3.5 h-3.5" aria-hidden="true" />
      <span>{label}</span>
    </Link>
  );
}
